import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import nav from '../nav';
import {
  lookupFacility, lookupProduct, lookupProvider, lookupPurchaseById, lookupSignupById, lookupUser
} from '../lookups';
import { ID_ROUTES, isGuid, looksLikeEmail } from '../identify';
import { readRecent, rememberRecent } from '../recent';
import { readAllViews } from '../savedViews';
import { REPORTS } from '../pages/Reports';

/**
 * ⌘K — one field that reaches anything in the admin.
 *
 * The prototype draws a search box in the top bar and the shell shipped it
 * disabled, because "search the admin" reads like it needs an endpoint nobody
 * has written. It does not. Almost every question somebody types into an admin
 * is one of three, and all three are answerable with what already exists:
 *
 *   1. **"Where is X?"** — the nav is a list of pages in this file's own
 *      import. Matching against it is free and instant, and it removes the main
 *      cost of a 30-item sidebar: reading all 30 to find one.
 *   2. **"Who/what is this name?"** — users, facilities and providers already
 *      have lookup endpoints that the filters use. Fanning out to the three and
 *      grouping the answers is the same three calls a person makes by hand,
 *      done at once.
 *   3. **"What is this id?"** — the real one. Ids arrive from Slack, from a
 *      customer email, from a log line, and *the id does not say what it is*.
 *      Today that means opening the entity pages one at a time until one of them
 *      is not a 404. Here five id-capable lookups run — signup, purchase, user,
 *      facility, provider — and whatever resolves is shown **by name**: not
 *      "this might be a signup" but "Hjortshøj · Mette Sørensen · 2026-08-19".
 *      Whatever does not resolve is offered as "open this id as…", one row per
 *      detail page that takes an id, so guessing wrong costs a click instead of
 *      a page load.
 *
 * Nothing here invents an endpoint or a payload. A group that cannot be answered
 * is not rendered.
 *
 * With an empty field it shows where you have been, from `localStorage` — the
 * page somebody wants next is very often the page they were on before the one
 * they are on now.
 *
 * Saved searches (`savedViews.js`) are matched here too, and they are the reason
 * this is more than a nav shortcut: "failed" reaching a saved *filtered list*
 * is a different tool from "failed" reaching a section.
 */

const MAX_PER_GROUP = 6;

// How far apart a subsequence match's letters may be, in total, before it stops
// being a match at all. Six is roughly one skipped word.
const MAX_SUBSEQUENCE_GAP = 6;

// Every nav destination, flattened, with the group it came from so the result
// can say "Money · Transactions" rather than just "Transactions".
const DESTINATIONS = nav.flatMap(({ group, items }) => items
  .filter(item => item.to)
  .map(item => ({
    kind: 'page',
    id: `page:${item.to}`,
    label: item.label,
    hint: group || 'Admin',
    to: `/next/${item.to}`
  })));

// Pages behind another page. Reports live two clicks in, under a menu, and their
// titles are what somebody actually types — "settlement", "seasonal court
// report" — none of which matches a nav entry.
const REPORT_PAGES = REPORTS.map(report => ({
  kind: 'page',
  id: `report:${report.to}`,
  label: report.title,
  hint: 'Reports',
  to: report.to
}));

// The create forms, which are pages nobody has in muscle memory because they are
// reached from a button on a list.
const ACTIONS = [
  { kind: 'action', id: 'new:facility', label: 'New facility', hint: 'Create', to: '/next/facility/new' },
  { kind: 'action', id: 'new:provider', label: 'New provider', hint: 'Create', to: '/next/provider/new' },
  { kind: 'action', id: 'new:area', label: 'New area', hint: 'Create', to: '/next/area/new' },
  { kind: 'action', id: 'new:locker', label: 'New locker', hint: 'Create', to: '/next/locker/new' },
  { kind: 'action', id: 'new:redirect', label: 'New facility redirect', hint: 'Create', to: '/next/facility-redirect/new' },
  { kind: 'action', id: 'new:seo', label: 'New SEO text', hint: 'Create', to: '/next/seo-text/new' },
  { kind: 'action', id: 'new:participant', label: 'New participant', hint: 'Create', to: '/next/participant/new' },
  { kind: 'action', id: 'new:crawling', label: 'Run a crawling', hint: 'Create', to: '/next/crawling/run' }
];

/**
 * How well a page title answers what was typed.
 *
 * Four ways to match, in the order somebody means them:
 *
 *   - **prefix** — "sea" for Seasonal Courts.
 *   - **initials** — "sco" for **S**easonal **C**ourts **O**verview. This one
 *     is not a nicety: it is how anybody types a three-word section name, and
 *     without it "sco" scored *Transactions* higher than the page it spells,
 *     because a plain subsequence match finds `tran**s**a**c**ti**o**ns` with
 *     smaller gaps. Initials therefore outrank everything but a prefix.
 *   - **contains**, with a word start worth more than a mid-word hit.
 *   - **subsequence**, last, for the letters-in-order case with nothing better.
 *
 * Shorter titles win ties, so "Users" beats "Provider Admin" for "us".
 */
const initialsOf = text => text
  .split(/[\s·/-]+/)
  .filter(Boolean)
  .map(word => word[0])
  .join('')
  .toLowerCase();

const score = (text, query) => {
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  if (!needle) return 0;
  if (haystack.startsWith(needle)) return 2000 - haystack.length;

  const initials = initialsOf(text);
  if (initials.startsWith(needle)) return 1500 - haystack.length;

  const wordStart = haystack.split(/[\s·/-]+/).some(word => word.startsWith(needle));
  if (haystack.includes(needle)) return (wordStart ? 900 : 600) - haystack.length;

  let index = 0;
  let gaps = 0;
  for (const character of needle) {
    const found = haystack.indexOf(character, index);
    if (found < 0) return -1;
    gaps += found - index;
    index = found + 1;
  }

  // The letters have to be near each other to mean anything. Without this,
  // "mette" — a person's name — matches "Pay*me*nt Me*t*hods" and "S*e*ttlement
  // report", and those two sit above the actual user in the results. A scatter
  // wide enough to need this much slack is a coincidence, not a match.
  if (gaps > MAX_SUBSEQUENCE_GAP) return -1;

  return 300 - gaps;
};

// The hint ("Money", "Courts") is worth matching but must not outweigh the
// title, or typing "money" ranks four pages above the one actually called it.
const scoreItem = (item, query) => Math.max(
  score(item.label, query),
  score(`${item.label} ${item.hint || ''}`, query) - 40
);

const ranked = (items, query) => items
  .map(item => ({ item, s: scoreItem(item, query) }))
  .filter(({ s }) => s >= 0)
  .sort((a, b) => b.s - a.s);

const rank = (items, query) => ranked(items, query).slice(0, MAX_PER_GROUP).map(({ item }) => item);

// Above this, what was typed is a page name rather than somebody's name, and
// the section should be the first thing offered. It is the floor of "contains,
// at a word start" — anything vaguer stays below the search results.
const NAVIGATION_CERTAIN = 800;

function CommandPaletteInternal({ api, open, onClose }) {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [found, setFound] = useState([]);
  const [busy, setBusy] = useState(false);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  // Guards a slow answer for an earlier keystroke from landing after a later one.
  const seq = useRef(0);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setFound([]);
      setActive(0);
      return undefined;
    }
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);

  const search = useCallback(async key => {
    const mine = seq.current + 1;
    seq.current = mine;
    setBusy(true);

    // Three independent lookups. One failing is normal — an id that is a
    // facility is not a user — so each settles on its own and a rejection
    // simply contributes nothing.
    const asRows = (rows, kind, route, hint) => (Array.isArray(rows) ? rows : [])
      .slice(0, MAX_PER_GROUP)
      .map(row => ({
        kind,
        id: `${kind}:${row.value}`,
        label: row.label || row.value,
        hint,
        to: route(row.value)
      }));

    // Signups and purchases are looked up only for a pasted id — they take an
    // id and answer at most one row, and a name is never an id. They go first
    // in the results because an id somebody pastes into an admin is most often
    // one of those two.
    const byId = isGuid(key)
      ? [
        lookupSignupById(api, key)
          .then(rows => asRows(rows, 'signup', id => `/next/signup/${id}`, 'Signup')).catch(() => []),
        lookupPurchaseById(api, key)
          .then(rows => asRows(rows, 'purchase', id => `/next/purchase/${id}`, 'Purchase')).catch(() => [])
      ]
      : [Promise.resolve([]), Promise.resolve([])];

    const [signups, purchases, users, facilities, providers, products] = await Promise.all([
      ...byId,
      lookupUser(api, key).then(rows => asRows(rows, 'user', id => `/next/user/${id}`, 'User')).catch(() => []),
      lookupFacility(api, key).then(rows => asRows(rows, 'facility', id => `/next/facility/${id}`, 'Facility')).catch(() => []),
      lookupProvider(api, key).then(rows => asRows(rows, 'provider', id => `/next/provider/${id}`, 'Provider')).catch(() => []),
      // A product resolves to a page in the *old* admin, so it carries an href
      // rather than a route — see `identify.js`.
      lookupProduct(api, key)
        .then(rows => (Array.isArray(rows) ? rows : []).slice(0, MAX_PER_GROUP).map(row => ({
          kind: 'product',
          id: `product:${row.value}`,
          label: row.label || row.value,
          hint: 'Product · old admin',
          href: `/product/${row.value}`
        })))
        .catch(() => [])
    ]);

    if (seq.current !== mine) return;
    setFound([...signups, ...purchases, ...users, ...facilities, ...providers, ...products]);
    setBusy(false);
  }, [api]);

  useEffect(() => {
    if (!open) return undefined;
    const key = query.trim();

    // Two characters is where a name search stops matching everything; an id or
    // an email is worth resolving as typed.
    if (key.length < 2) {
      setFound([]);
      return undefined;
    }

    const timer = setTimeout(() => search(key), isGuid(key) || looksLikeEmail(key) ? 0 : 260);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  const groups = useMemo(() => {
    const key = query.trim();

    if (!key) {
      const recent = readRecent()
        .map(entry => ({ kind: 'recent', id: `recent:${entry.to}`, label: entry.label, hint: entry.hint, to: entry.to }))
        .slice(0, 5);

      const saved = readAllViews().slice(0, 5).map(view => ({
        kind: 'saved',
        id: `saved:${view.screenPath}${view.search}:${view.name}`,
        label: view.name,
        hint: 'Saved search',
        to: `${view.screenPath}${view.search}`
      }));

      return [
        saved.length ? { title: 'Saved searches', items: saved } : null,
        recent.length ? { title: 'Recent', items: recent } : null,
        { title: 'Go to', items: DESTINATIONS.slice(0, 8) }
      ].filter(Boolean);
    }

    // Saved searches are pages as far as this is concerned — a name somebody
    // chose, pointing at a URL.
    const saved = readAllViews().map(view => ({
      kind: 'saved',
      id: `saved:${view.screenPath}${view.search}:${view.name}`,
      label: view.name,
      hint: 'Saved search',
      to: `${view.screenPath}${view.search}`
    }));

    const pageHits = ranked([...saved, ...DESTINATIONS, ...REPORT_PAGES], key);
    const pages = pageHits.slice(0, MAX_PER_GROUP).map(({ item }) => item);
    const actions = rank(ACTIONS, key);

    // "sig" is somebody heading for Signups; "mette" is somebody looking for a
    // person. Whichever is more certain goes first, so Enter does the obvious
    // thing without reading the list.
    const navigating = (pageHits[0]?.s ?? -1) >= NAVIGATION_CERTAIN;

    // An id that resolved is a real answer and goes first; the "open as" rows
    // below it are guesses and say so.
    const guessed = isGuid(key)
      ? ID_ROUTES
        .map(route => (route.to
          ? { kind: 'guess', id: `guess:${route.label}`, label: route.label, to: route.to(key) }
          : { kind: 'guess', id: `guess:${route.label}`, label: route.label, href: route.href(key) }))
        .filter(row => !found.some(hit => (row.to ? hit.to === row.to : hit.href === row.href)))
      : [];

    const goTo = pages.length ? { title: 'Go to', items: pages } : null;
    const foundGroup = found.length ? { title: 'Found', items: found } : null;

    return [
      ...(navigating ? [goTo, foundGroup] : [foundGroup, goTo]),
      actions.length ? { title: 'Create', items: actions } : null,
      guessed.length
        ? {
          title: 'Open this id as…',
          // Said once for the group instead of on all twenty-odd rows, where it
          // becomes wallpaper and stops being read.
          note: 'None of these was found by a lookup — the page may be empty.',
          items: guessed,
          quiet: true
        }
        : null
    ].filter(Boolean);
  }, [query, found]);

  const flat = useMemo(() => groups.flatMap(group => group.items), [groups]);

  useEffect(() => {
    setActive(0);
  }, [query, found.length]);

  // Keep the highlighted row in view when it moves past the fold.
  useEffect(() => {
    const node = listRef.current?.querySelector('[data-active="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [active, groups]);

  if (!open) return null;

  const go = item => {
    if (!item) return;
    // Recents are the pages of this admin, not every entity ever opened: an
    // entity row is a one-off, a section is somewhere you return to.
    if (item.kind === 'page' || item.kind === 'action' || item.kind === 'recent') {
      rememberRecent({ to: item.to, label: item.label, hint: item.hint });
    }
    onClose();

    // A target in the old admin is a full page load, not a route: going through
    // the router would mount it inside a shell that was never meant to hold it.
    if (item.href) {
      window.location.assign(item.href);
      return;
    }

    // A saved search differs from where you are only in the query string, and
    // navigating to the same pathname with a different search is the case that
    // leaves a mounted list showing its old filters. `replace: false` plus the
    // full string is what the list pages already read.
    navigate(item.to, { replace: false });
  };

  const onKeyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(current => (flat.length ? (current + 1) % flat.length : 0));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(current => (flat.length ? (current - 1 + flat.length) % flat.length : 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      go(flat[active]);
    }
  };

  let cursor = -1;

  return (
    // Not portalled, for the same reason `Modal` is not: everything in this tree
    // has to render inside `.next-root` or it is painted by the old stylesheet.
    <div
      className="next-palette__scrim"
      role="presentation"
      onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className="next-palette" role="dialog" aria-modal="true" aria-label="Search the admin">
        <div className="next-palette__field">
          <span className="next-palette__icon" aria-hidden>⌕</span>
          <input
            ref={inputRef}
            className="next-palette__input"
            value={query}
            placeholder="Search a page, a name, or paste an id"
            aria-label="Search a page, a name, or paste an id"
            onChange={event => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
          />
          {busy && <span className="next-palette__busy" aria-hidden>…</span>}
          <button type="button" className="next-palette__esc" onClick={onClose}>esc</button>
        </div>

        <div className="next-palette__list" ref={listRef}>
          {flat.length === 0 && (
            <p className="next-palette__empty">
              {query.trim().length < 2
                ? 'Type at least two characters.'
                : (busy ? 'Searching…' : 'Nothing matches. A name, an email or an id all work.')}
            </p>
          )}

          {groups.map(group => (
            <div className="next-palette__group" key={group.title}>
              <div className="next-palette__grouptitle">{group.title}</div>
              {group.note && <div className="next-palette__groupnote">{group.note}</div>}
              {group.items.map(item => {
                cursor += 1;
                const index = cursor;
                return (
                  <button
                    type="button"
                    key={item.id}
                    data-active={index === active}
                    className={`next-palette__item${index === active ? ' next-palette__item--active' : ''}${group.quiet ? ' next-palette__item--quiet' : ''}`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(item)}
                  >
                    <span className="next-palette__label">{item.label}</span>
                    {item.hint && <span className="next-palette__hint">{item.hint}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/*
          Words rather than `↑ ↓ ↵`: the arrows depend on whichever font the
          browser falls back to, and a hint line is not worth the gamble. (The
          empty caps this first produced were not the font at all — see the
          `kbd` rule in master.scss.)
        */}
        <div className="next-palette__foot">
          <span><kbd>Up</kbd><kbd>Down</kbd> move</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}

CommandPaletteInternal.propTypes = {
  api: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired
};

export default withApi(CommandPaletteInternal);
